import React, {FC} from 'react';

import {DropdownItemIcon} from '../../../../../../../src/components/common/dropdowns/dropdownItemIcon';
import {ExportCardsJobOptionTypesEnum} from '../../../../../../../src/models/api/jobModel';
import {Trans} from '../../../../../../shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../../shared/helpers/src/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../../../../shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Dropdown} from '../../../../../../shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../../../shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../../shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../../../shared/ui/src/components/dropdowns/dropdownList';
import {PopoverTransferProps} from '../../../../../../shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../shared/ui/src/components/tooltips/tooltipCoordinator';
import {UserThemeConsumer} from '../../../../../../shared/ui/src/theme/userThemeConsumer';
import {styled, useUserTheme} from '../../../../../../shared/ui/src/theme/userThemeProvider';

/*
 * Constants.
 */

const sharedExportTooltipWidth = 240; // px.
const defaultDropdownWidth = 260; // px.

/*
 * Props.
 */

interface ExportContactsDropdownProps {
  selectedExportType: ExportCardsJobOptionTypesEnum;
  onSetExportType: HandlerOf<ExportCardsJobOptionTypesEnum>;
  hasError: boolean;
}

/*
 * Style.
 */

const StyledValueDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledDropdownItem = styled(DropdownItem)`
  padding-left: 15px;
`;

const StyledDropdownBoxCoordinator = styled(DropdownBoxCoordinator)`
  padding: 0px;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  margin-left: 6px;
`;

/*
 * Component.
 */

export const ExportContactsDropdown: FC<ExportContactsDropdownProps> = ({
  selectedExportType,
  hasError,
  onSetExportType,
}) => {
  const theme = useUserTheme();

  return (
    <StyledDropdownBoxCoordinator
      value={renderValue(selectedExportType, false, true)}
      renderDropdown={(popoverProps) => renderDropdown(popoverProps, selectedExportType, onSetExportType)}
      backgroundColor={hasError ? theme.palette.red.shade10 : undefined}
      backgroundHoverColor={hasError ? theme.palette.red.shade20 : undefined}
    />
  );
};

/*
 * Helpers.
 */

function renderDropdown(
  popoverProps: PopoverTransferProps,
  selectedExportType: ExportCardsJobOptionTypesEnum,
  onSetExportType: HandlerOf<ExportCardsJobOptionTypesEnum>,
) {
  return (
    <Dropdown {...popoverProps} width={defaultDropdownWidth}>
      <DropdownList {...popoverProps}>
        {renderDropdownItem(ExportCardsJobOptionTypesEnum.COMPANY, selectedExportType, onSetExportType)}
        {renderDropdownItem(ExportCardsJobOptionTypesEnum.PRIVATE, selectedExportType, onSetExportType)}
        {renderDropdownItem(ExportCardsJobOptionTypesEnum.SHARED, selectedExportType, onSetExportType)}
      </DropdownList>
    </Dropdown>
  );
}

function renderDropdownItem(
  exportType: ExportCardsJobOptionTypesEnum,
  selectedExportType: ExportCardsJobOptionTypesEnum,
  onSetExportType: HandlerOf<ExportCardsJobOptionTypesEnum>,
) {
  const isChecked = selectedExportType === exportType;
  return (
    <StyledDropdownItem isChecked={isChecked} onSelect={() => onSetExportType(exportType)}>
      {renderValue(exportType, isChecked)}
    </StyledDropdownItem>
  );
}

function renderValue(
  exportType: ExportCardsJobOptionTypesEnum,
  isChecked = false,
  renderAsSelectedValue = false,
) {
  switch (exportType) {
    case ExportCardsJobOptionTypesEnum.PRIVATE:
      return (
        <UserThemeConsumer>
          {(theme) => (
            <StyledValueDiv>
              <DropdownItemIcon
                label=""
                iconName="lockFilledTiny"
                iconSize={16}
                color={theme.greys.shade70}
              />
              <DropdownItemContent isChecked={false}>
                <Trans id="export-private-contacts-dropdown-item-label">Private contacts</Trans>
              </DropdownItemContent>
            </StyledValueDiv>
          )}
        </UserThemeConsumer>
      );
    case ExportCardsJobOptionTypesEnum.SHARED:
      return (
        <UserThemeConsumer>
          {(theme) => (
            <StyledValueDiv>
              <DropdownItemIcon
                label=""
                iconName="contactsFilledSmall"
                color={theme.greys.shade70}
                iconSize={16}
              />
              <DropdownItemContent isChecked={isChecked}>
                <Trans id="export-shared-and-auto-created-contacts-dropdown-item-label">
                  Shared and auto-created
                </Trans>
              </DropdownItemContent>
              {!renderAsSelectedValue && (
                <StyledTooltipCoordinator
                  render={renderSharedExportTooltip}
                  placement="top"
                  maxWidth={sharedExportTooltipWidth}
                  persistent
                />
              )}
            </StyledValueDiv>
          )}
        </UserThemeConsumer>
      );

    case ExportCardsJobOptionTypesEnum.COMPANY:
    default:
      return (
        <UserThemeConsumer>
          {(theme) => (
            <StyledValueDiv>
              <DropdownItemIcon
                label=""
                iconName="sharedFilledSmall"
                color={theme.greys.shade70}
                iconSize={16}
              />
              <DropdownItemContent isChecked={isChecked}>
                <Trans id="export-shared-contacts-dropdown-item-label">Shared contacts</Trans>
              </DropdownItemContent>
            </StyledValueDiv>
          )}
        </UserThemeConsumer>
      );
  }
}

function renderSharedExportTooltip() {
  return (
    <Trans id="export-shared-contacts-dropdown-item-tooltip">
      Includes Shared contacts and auto-created contacts that were created in shared inboxes you have access
      to.
    </Trans>
  );
}
